/* eslint-disable react-hooks/exhaustive-deps,jsx-a11y/control-has-associated-label,import/no-unresolved */
/* eslint-disable camelcase */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useQuery } from 'react-query';
import { LTVPercentSelect } from 'assets/components';
import moment from 'moment';
import { Tooltip } from 'react-tooltip';
import { currencies, exchange } from '../../../../utilities/constants';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { useRate } from '../../../../utilities/sharedHooks/rates';
import {
  formatDecimalNumber,
  formatNumber,
  formatNumberWithComma,
} from '../../../../utilities/formatHelpers';
import { USER_PREFERENCES } from '../../../../utilities/sharedQueries';
import {
  LOAN_AMOUNT_STEP,
  LTV,
  LTV_PERCENTAGES,
  MAX_LOAN_PERCENTAGE,
  MODAL_SCREENS,
} from '../../constants';
import { useLoanConditions } from '../../hooks';
import { calculateClosestLtv, calculateTotalInterestValue } from '../../helpers';
import RadioItem from '../../../../components/RadioItem';
import { FLOWS, sendStats } from '../../../../services/mixpanel';
import { MXP_FTC_CHOSE_COLLATERAL_SOURCE } from '../../mixpanel-events';
import FocusableInputArea from '../../../Accounts/partials/FocusableInputArea';
import { AMOUNT_INPUT_MAX_LENGTH } from '../../../Accounts/constants';
import { validateNumbers } from '../../../../utilities/formValidation';
import { GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS } from '../../../Staking/queries';
import { mainStakingCurrency } from '../../../Staking/constants';
import { dynamicAssets } from '../../../../utilities/helpers';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';

const LoanLengthSelect = ({ loanLength, onChangeLoanLength, loanDurations }) => {
  const { t: translate } = useTranslation();

  const date = moment(new Date()).add(loanLength, 'M').format('DD MMM YYYY');

  return (
    <>
      {/* mobile layout */}
      <div className="d-flex d-md-none flex-row justify-content-between gap-2 w-100 overflow-auto hide-scroll mt-2">
        {loanDurations?.map((duration) => (
          <RadioItem
            style={{
              minWidth: `${loanLength === duration ? '10.5rem' : '7.5rem'}`,
              maxHeight: '4.5rem',
            }}
            key={duration}
            className="d-flex justify-content-center ps-2"
            label={` ${duration}${
              loanLength === duration
                ? ` ${translate('loan_review_offer_month_label').toLowerCase()}`
                : translate('m')
            }`}
            subtitle={
              loanLength === duration
                ? translate('loan_calculator_due_date').replace('__DATE__', date)
                : ''
            }
            name="loanLength"
            onChange={() => onChangeLoanLength(duration)}
            checked={loanLength === duration}
          />
        ))}
      </div>

      {/* browser layout */}
      <div className="d-none d-md-flex flex-row flex-wrap justify-content-between w-100 mt-2">
        {loanDurations?.map((duration) => (
          <RadioItem
            key={duration}
            style={{ minHeight: '5rem' }}
            className="d-flex pe-2 w-45 align-items-center mt-1"
            label={` ${duration}${
              loanLength === duration
                ? ` ${translate('loan_review_offer_month_label').toLowerCase()}`
                : 'm'
            }`}
            subtitle={
              loanLength === duration
                ? translate('loan_calculator_due_date').replace('__DATE__', date)
                : ''
            }
            name="loanLength"
            onChange={() => onChangeLoanLength(duration)}
            checked={loanLength === duration}
          />
        ))}
      </div>
    </>
  );
};

LoanLengthSelect.propTypes = {
  loanLength: PropTypes.string.isRequired,
  onChangeLoanLength: PropTypes.func.isRequired,
  loanDurations: PropTypes.array.isRequired,
};

const LoanCalculator = ({
  screensKeys,
  calculatorForm,
  updateCalculatorForm,
  openScreen,
  wallets,
  displayAddCollateral,
  setExtraCollateralOptions,
}) => {
  const { t: translate } = useTranslation();
  const principalCurrencyObj = calculatorForm?.principalWallet;
  const principalCurrency = principalCurrencyObj?.currency_code;

  const { data: activeStakingAmountTiers, refetch: refetchActiveStakingAmountTiers } = useQuery(
    GET_ACTIVE_STAKING_AMOUNT_WITH_TIERS,
    {
      enabled: false,
    }
  );

  const principalBalance = useMemo(() => {
    const wallet = wallets.find((w) => w.coin === principalCurrency);
    return (wallet?.available_balance || 0) * currencies[principalCurrency].conversion;
  }, [wallets, principalCurrency]);

  const collateralWallet = calculatorForm?.collateralWallet;
  const collateralCurrency = collateralWallet.coin;
  const collateralBalance =
    collateralWallet.available_balance * currencies[collateralCurrency].conversion;

  const step = LOAN_AMOUNT_STEP;

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const displayCurrency = userPref?.displayCurrency;

  const [fiatPrincipalInputWidth, setFiatPrincipalInputWidth] = useState(0);
  const [principalInputWidth, setPrincipalInputWidth] = useState(0);

  const [showMinLoanPayoutAmountError, setShowMinLoanPayoutAmountError] = useState(false);
  const [showMaxLoanPayoutAmountError, setShowMaxLoanPayoutAmountError] = useState(false);
  const [hasSufficientCollateral, setHasSufficientCollateral] = useState();

  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [showPayoutInfoPopup, setShowPayoutInfoPopup] = useState(false);

  const [selectedLTV, setLTV] = useState(LTV.LTV_10);

  const principalToDisplayCurrencyRate = useRate(principalCurrency, displayCurrency);
  const collateralToDisplayCurrencyRate = useRate(collateralCurrency, displayCurrency);
  const principalToCollateralRate = useRate(principalCurrency, collateralCurrency);
  const collateralToPrincipalRate = useRate(collateralCurrency, principalCurrency);

  const { minLoan, maxLoan, loanDurations, ...rest } = useLoanConditions(
    collateralBalance * collateralToPrincipalRate,
    principalCurrency
  );

  const LTVToAnnualInterest = useRef({
    ...rest,
  }).current;

  const ltvPercentList = LTV_PERCENTAGES;

  const loanDurationsOptions = loanDurations?.split(',');
  const initLoanLength =
    loanDurationsOptions && loanDurationsOptions?.length > 0 ? loanDurationsOptions[0] : null;
  const [loanLength, setLoanLength] = useState(initLoanLength);
  useEffect(() => {
    if (!calculatorForm?.principalAmount) {
      updateCalculatorForm((prev) => ({
        ...prev,
        principalAmount: minLoan,
        principalAmountDecimal: minLoan,
      }));
    }
  }, [minLoan]);

  useEffect(() => {
    setShowMinLoanPayoutAmountError(calculatorForm?.principalAmountDecimal < minLoan);
  }, [calculatorForm?.principalAmountDecimal, minLoan]);

  useEffect(() => {
    setShowMaxLoanPayoutAmountError(calculatorForm?.principalAmountDecimal > maxLoan);
  }, [calculatorForm?.principalAmountDecimal, maxLoan]);

  const loanInterestValue = formatNumber(
    calculateTotalInterestValue(
      calculatorForm?.apr,
      loanLength,
      calculatorForm?.principalAmountDecimal
    ),
    exchange.FIAT_VALUE
  );

  const sliderDisabled = maxLoan === minLoan;

  let sliderValue;
  if (sliderDisabled) {
    sliderValue = minLoan;
  } else if (calculatorForm?.principalAmountDecimal < minLoan) {
    sliderValue = minLoan;
  } else if (calculatorForm?.principalAmountDecimal > maxLoan) {
    sliderValue = maxLoan;
  } else {
    sliderValue = parseFloat(calculatorForm?.principalAmountDecimal);
  }

  const fiatPrincipalInputSpan = useRef();
  const collateralInputSpan = useRef();

  const ltv = ltvPercentList[selectedLTV];
  const requiredCollateralAmountFiat = useMemo(
    () => calculatorForm?.principalAmountDecimal / (ltv / 100.0),
    [calculatorForm?.principalAmountDecimal, ltv]
  );

  const requiredCollateralRaw = useMemo(
    () => requiredCollateralAmountFiat * principalToCollateralRate,
    [requiredCollateralAmountFiat, principalToCollateralRate]
  );

  const maxAllowedLoanAmount = formatNumber(
    collateralBalance * collateralToDisplayCurrencyRate * MAX_LOAN_PERCENTAGE,
    exchange.FIAT_VALUE
  );

  const principalInCollateralCurrency = formatNumber(
    (calculatorForm?.principalAmount || minLoan) * principalToCollateralRate,
    currencies[collateralCurrency].decimal
  );

  const [principalInCollateralCurrencyAmount, setPrincipalInCollateralCurrencyAmount] = useState(
    principalInCollateralCurrency
  );

  const { isUS } = useUserPersonalInfo();

  const isDepositHidden =
    process.env.REACT_APP_DEPOSIT_BUTTON_HIDDEN &&
    !isUS &&
    !['usdc', 'euroc'].includes(collateralCurrency?.toLowerCase());

  useEffect(() => {
    refetchActiveStakingAmountTiers();
  }, []);

  useEffect(() => {
    if (collateralCurrency !== mainStakingCurrency) {
      let ltvStartingIndex = 0;
      const dbLTVs = Object.values(LTV_PERCENTAGES);
      // eslint-disable-next-line no-plusplus
      for (let index = 0; index < dbLTVs.length - 1; index++) {
        if (
          activeStakingAmountTiers &&
          activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
          activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free > dbLTVs[index]
        ) {
          ltvStartingIndex += 1;
        }
      }
      const calculatedLTV = calculateClosestLtv(
        calculatorForm?.principalAmountDecimal / principalBalance,
        ltvStartingIndex
      );
      setLTV(calculatedLTV);
    } else {
      setLTV(LTV.LTV_10);
    }
  }, [calculatorForm?.principalAmountDecimal, principalBalance, collateralCurrency]);

  useEffect(() => {
    if (
      activeStakingAmountTiers &&
      activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free &&
      collateralCurrency !== mainStakingCurrency
    ) {
      setLTV(LTV[`LTV_${activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free}`]);
    } else if (collateralCurrency === mainStakingCurrency) {
      setLTV(LTV.LTV_10);
    }
  }, [activeStakingAmountTiers]);

  useEffect(() => {
    if (selectedLTV) {
      let apr = parseFloat(LTVToAnnualInterest[selectedLTV]);
      if (
        (activeStakingAmountTiers &&
          activeStakingAmountTiers?.userStakingTier &&
          ltvPercentList[selectedLTV] ===
            activeStakingAmountTiers?.userStakingTier?.stableloan_ltv_interest_free) ||
        (mainStakingCurrency === collateralCurrency &&
          ltvPercentList[selectedLTV] === LTV_PERCENTAGES.interest_rate_ltv_10)
      ) {
        apr = 0;
      }

      updateCalculatorForm((prev) => ({
        ...prev,
        apr,
      }));
    }
  }, [LTVToAnnualInterest, selectedLTV, collateralCurrency]);

  useEffect(() => {
    setHasSufficientCollateral(collateralBalance >= requiredCollateralRaw);

    if (requiredCollateralRaw > collateralBalance) {
      setExtraCollateralOptions({
        collateralWallet,
        minCollateralAmountNeeded: requiredCollateralRaw - collateralBalance,
      });
    }
  }, [collateralBalance, requiredCollateralRaw]);

  const allowProceed =
    !showMinLoanPayoutAmountError &&
    collateralBalance >= requiredCollateralRaw &&
    calculatorForm?.principalAmountDecimal >= minLoan &&
    calculatorForm?.principalAmountDecimal <= maxLoan;

  const handleChangePrincipalInCollateralCurrency = (e) => {
    const { value: amount } = e.target;

    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, 0);

    if (!validateNumbers(decimalAmount, collateralWallet?.wallet_type)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      updateCalculatorForm((prev) => ({
        ...prev,
        principalAmount: 0,
        principalAmountDecimal: 0,
      }));

      setPrincipalInCollateralCurrencyAmount(0);
    }

    const calcFiat = formatNumber(decimalAmount * collateralToPrincipalRate, exchange.FIAT_VALUE);
    const formattedFiatCalc = formatDecimalNumber(calcFiat);

    updateCalculatorForm((prev) => ({
      ...prev,
      principalAmount: formattedFiatCalc.formattedAmount,
      principalAmountDecimal: formattedFiatCalc.decimalAmount,
    }));
    setPrincipalInCollateralCurrencyAmount(formattedAmount);
  };

  const handleChangeFiatAmount = (event) => {
    const { value: amount, min } = event.target;
    const { decimalAmount, formattedAmount } = formatDecimalNumber(amount, min);

    if (!validateNumbers(decimalAmount, principalCurrencyObj?.currency_type)) {
      return;
    }

    if (!decimalAmount || !formattedAmount) {
      setPrincipalInCollateralCurrencyAmount(0);
      updateCalculatorForm((prev) => ({
        ...prev,
        principalAmount: 0,
        principalAmountDecimal: 0,
      }));
    }

    const calcCrypto = formatNumber(
      decimalAmount / collateralToPrincipalRate,
      exchange.CRYPTO_VALUE
    );
    const formattedCalcCrypto = formatDecimalNumber(calcCrypto);

    setPrincipalInCollateralCurrencyAmount(formattedCalcCrypto.formattedAmount);
    updateCalculatorForm((prev) => ({
      ...prev,
      principalAmount: formattedAmount,
      principalAmountDecimal: decimalAmount,
    }));
  };

  useEffect(() => {
    const charactersLength =
      fiatPrincipalInputSpan.current.value.length > 1
        ? fiatPrincipalInputSpan.current.value.length
        : 1;
    // to avoid having empty input, fix length to 1 if text length is less then 1
    setFiatPrincipalInputWidth(charactersLength);
  }, [calculatorForm?.principalAmount]);

  useEffect(() => {
    const closestContainer = collateralInputSpan.current.closest('.d-flex');
    const closestContainerParent = closestContainer.closest('.modal-body');
    const charactersLength =
      collateralInputSpan.current.value.length > 1 ? collateralInputSpan.current.value.length : 2;
    // to avoid having empty input, fix length to 2 if text length is less then 1
    if (closestContainerParent.offsetWidth < closestContainer.scrollWidth) {
      closestContainer.style.fontSize = '1.5rem';
    } else if (collateralInputSpan.current.value.length < AMOUNT_INPUT_MAX_LENGTH) {
      // resize input font size to 2.125rem when char length is less then AMOUNT_INPUT_MAX_LENGTH
      closestContainer.style.fontSize = '2.125rem';
    }
    setPrincipalInputWidth(charactersLength);
  }, [principalInCollateralCurrencyAmount]);

  const handleFocus = (e) => {
    if (!e.target) return;
    e.target.select();
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.add('active');
    }
  };

  const handleBlurAmount = (e) => {
    if (!e.target) return;
    if (e.target.closest('.amount-input')) {
      e.target.closest('.amount-input').classList.remove('active');
    }
  };

  const renderFooter = () => (
    <button
      className={`btn ${allowProceed ? 'btn-primary' : 'btn-disabled'}`}
      type="button"
      disabled={!allowProceed}
      onClick={() => {
        updateCalculatorForm((prev) => ({
          ...prev,
          principalAmount: calculatorForm?.principalAmountDecimal,
          collateralAmount: requiredCollateralRaw,
          loanLength,
          totalInterest: loanInterestValue,
          ltv: ltvPercentList[selectedLTV],
        }));
        openScreen(screensKeys.LOAN_OFFER);
      }}>
      {translate('stableloan_calculator_get_btn_label')}
    </button>
  );

  const renderTitle = () => (
    <div className="d-flex flex-row justify-content-between w-100">
      <b className="text-f5 text-dark">{translate('stable_loan_calculator_title')}</b>
      {/* browser */}
      <div
        className="d-none d-md-block"
        role="button"
        data-tooltip-id="loan-calc-info-tooltip"
        tabIndex={-1}
        onMouseEnter={() => setShowInfoPopup(true)}
        onKeyDown={null}
        onMouseLeave={() => setShowInfoPopup(false)}>
        <i
          className={`${
            showInfoPopup ? 'text-primary' : 'text-shadow'
          } icon-question-other ms-auto text-f4 `}
        />
      </div>

      {/* mobile */}
      <div
        className="d-block d-md-none"
        role="button"
        tabIndex={-1}
        onClick={() => openScreen(MODAL_SCREENS.DETAILS_INFO)}
        onKeyDown={null}>
        <i
          className={`icon icon-question-other text-shadow
            text-f4 ms-auto pe-4`}
        />
      </div>
    </div>
  );

  return (
    <>
      <ModalBodyLayout withBack={false} renderTitle={renderTitle} renderFooter={renderFooter}>
        <div>
          <div className="d-flex flex-row mt-4">
            <p className="text-shadow text-f5">
              {translate('loan_calculator_choose_payment_source')}
            </p>
            {/* browser only */}
            <div
              className="d-none d-md-block"
              data-tooltip-id="payment-source-tooltip"
              role="button"
              tabIndex={-1}
              onMouseEnter={() => setShowPayoutInfoPopup(true)}
              onKeyDown={null}
              onMouseLeave={() => setShowPayoutInfoPopup(false)}>
              <i
                className={`${
                  showPayoutInfoPopup ? 'text-primary' : 'text-shadow'
                } icon-question-other ms-2 text-f4`}
              />
            </div>

            {/* mobile only */}
            <div
              className="d-block d-md-none"
              role="button"
              tabIndex={-1}
              onClick={() => openScreen(MODAL_SCREENS.PAYOUT_DETAILS_INFO)}
              onKeyDown={null}>
              <i
                className={`text-shadow
                icon-question-other ms-2 text-f4`}
              />
            </div>
          </div>
        </div>
        <div className="mt-3">
          <div className="select-group bg-light rounded-extra p-3 d-flex flex-row mt-2 mb-4 select-currency-container">
            <div
              role="button"
              tabIndex="0"
              className="d-flex flex-row align-items-center px-2 card-calculator__select-currency"
              onClick={() => {
                updateCalculatorForm((prev) => ({
                  ...prev,
                  isPayoutWallet: true,
                }));
                openScreen(screensKeys.WALLETS);
              }}
              onKeyDown={null}>
              <img
                src={dynamicAssets(`./assets/icons/${principalCurrency?.toUpperCase()}.svg`)}
                className="me-2"
                alt={principalCurrency}
              />
              <p className="m-0 me-2 text-f5 upper-case">{principalCurrency}</p>
              <i className="icon icon-arrow-down-navigation" />
            </div>
            <span className="text-right flex-fill">
              <b className="text-f5 m-0 upper-case">
                {formatNumberWithComma(
                  formatNumber(principalBalance, currencies[principalCurrency].decimal)
                )}{' '}
                {principalCurrency}
              </b>
              <p className="text-shadow text-f6 m-0 upper-case">
                {formatNumberWithComma(
                  formatNumber(
                    principalBalance * principalToDisplayCurrencyRate,
                    exchange.FIAT_VALUE
                  )
                )}{' '}
                {displayCurrency}
              </p>
            </span>
          </div>
        </div>
        <p className="text-shadow text-f5 mt-4">{translate('loan_calculator_enter_amount_note')}</p>
        <FocusableInputArea className="mt-4 mb-1">
          <input
            required
            type="text"
            className="form-control bg-transparent p-0 text-shadow text-center border-0"
            value={principalInCollateralCurrencyAmount}
            style={{
              fontSize: '1rem',
              width: `${principalInputWidth + 1}ch`,
              height: '1.5rem',
            }}
            ref={collateralInputSpan}
            onChange={(e) => handleChangePrincipalInCollateralCurrency(e)}
            placeholder="0 "
          />
          <span
            style={{ fontSize: 'inherit', pointerEvents: 'none' }}
            className="text-f5 text-shadow">
            {collateralCurrency?.toUpperCase()}
          </span>
        </FocusableInputArea>
        <FocusableInputArea className="h1 amount-input">
          <input
            required
            type="text"
            className="form-control bg-transparent text-bold p-0 text-center border-0"
            value={calculatorForm?.principalAmount}
            style={{
              fontSize: 'inherit',
              width: `${fiatPrincipalInputWidth + 1}ch`,
              color: 'inherit',
              height: 'inherit',
              textIndent: calculatorForm?.principalAmount ? 0 : '34%',
            }}
            ref={fiatPrincipalInputSpan}
            onChange={(e) => handleChangeFiatAmount(e)}
            onFocus={handleFocus}
            onBlur={handleBlurAmount}
            placeholder="0  "
          />
          <b
            style={{
              fontSize: 'inherit',
              pointerEvents: 'none',
              color: 'inherit',
            }}>
            {principalCurrency?.toUpperCase()}
          </b>
        </FocusableInputArea>
        <div className="mb-2">
          <div className="slider-progress">
            <div className="progress">
              <div
                className="progress-bar"
                style={{
                  width: `${
                    !sliderDisabled ? ((sliderValue - minLoan) * 100) / (maxLoan - minLoan) : 0
                  }%`,
                }}
              />
            </div>
            <input
              type="range"
              className="form-range"
              value={sliderValue}
              max={maxLoan}
              min={minLoan}
              step={step}
              disabled={sliderDisabled}
              onChange={(e) => {
                handleChangeFiatAmount(e);
              }}
            />
          </div>
        </div>
        {showMinLoanPayoutAmountError && (
          <div className="alert alert-info alert-action mt-lg-4">
            <i className="icon icon-info-icon" />
            <span className="content">
              {translate('loan_calculator_minimum_loan_note').replace(
                '__MIN_LOAN__',
                `${
                  currencies[principalCurrency].symbol ||
                  `${currencies[principalCurrency].fullName} `
                }${minLoan}`
              )}
            </span>
          </div>
        )}
        {showMaxLoanPayoutAmountError && maxAllowedLoanAmount >= minLoan && (
          <div className="alert alert-info alert-action mt-lg-4">
            <i className="icon icon-info-icon" />
            <span className="content">
              {translate('loan_calculator_maximum_loan_note').replace(
                '__MAX_LOAN__',
                `${maxAllowedLoanAmount} ${currencies[principalCurrency].fullName}`
              )}
            </span>
          </div>
        )}
        <p className="text-shadow text-f5 mt-4">
          {translate('loan_calculator_source_payment_note')}
        </p>
        <div className="mt-3">
          <div className="select-group bg-light rounded-extra p-3 d-flex flex-row mt-2 mb-4 select-currency-container">
            <div
              role="button"
              tabIndex="0"
              className="d-flex flex-row align-items-center px-2 card-calculator__select-currency"
              onClick={() => {
                sendStats(MXP_FTC_CHOSE_COLLATERAL_SOURCE, {}, FLOWS.FTC);
                updateCalculatorForm((prev) => ({
                  ...prev,
                  isPayoutWallet: false,
                  allowBXXAsCollateral:
                    activeStakingAmountTiers?.userStakingTier?.allow_bxx_as_stableloan_collateral ||
                    false,
                }));
                openScreen(screensKeys.WALLETS);
              }}
              onKeyDown={null}>
              <img
                src={dynamicAssets(`./assets/icons/${collateralCurrency?.toUpperCase()}.svg`)}
                className="me-2"
                alt={collateralCurrency}
              />
              <p className="m-0 me-2 text-f5 upper-case">{collateralCurrency}</p>
              <i className="icon icon-arrow-down-navigation" />
            </div>
            <span className="text-right flex-fill">
              <b className="text-f5 m-0 upper-case">
                {formatNumber(collateralBalance, currencies[collateralCurrency].decimal)}{' '}
                {collateralCurrency}
              </b>
              <p className="text-shadow text-f6 m-0 upper-case">
                {formatNumberWithComma(
                  formatNumber(
                    collateralBalance * collateralToDisplayCurrencyRate,
                    exchange.FIAT_VALUE
                  )
                )}{' '}
                {displayCurrency}
              </p>
            </span>
          </div>
        </div>
        <div className="d-flex flex-row justify-content-between w-100">
          <p className="text-shadow text-f5">{translate('loan_calculator_locked_collateral')}</p>
          <span className="text-right flex-fill">
            <b className="text-f5 m-0 upper-case">
              {formatNumber(requiredCollateralRaw, currencies[collateralCurrency].decimal)}{' '}
              {collateralCurrency}
            </b>
            <p className="text-shadow text-f6 m-0 upper-case">
              {collateralToPrincipalRate
                ? formatNumberWithComma(
                    formatNumber(
                      requiredCollateralAmountFiat,
                      currencies[principalCurrency].decimal
                    )
                  )
                : '-'}{' '}
              {principalCurrency}
            </p>
          </span>
        </div>
        {hasSufficientCollateral !== true && !isDepositHidden && (
          <div
            role="button"
            tabIndex="0"
            onClick={displayAddCollateral}
            onKeyDown={displayAddCollateral}
            className="alert alert-info alert-action mt-lg-4">
            <i className="icon icon-info-icon" />
            <span className="content">
              {translate('loan_calculator_collateral_source_insufficient_balance').replace(
                '__COLLATERAL_CURRENCY__',
                collateralCurrency?.toUpperCase()
              )}
            </span>
            <i className="icon icon-arrow-next-navigation text-right flex-fill" />
          </div>
        )}
        <LTVPercentSelect
          ltv={selectedLTV}
          onChangePercent={(val) => {
            setLTV(val);
          }}
          ltvPercentList={ltvPercentList}
          tierLevel={activeStakingAmountTiers}
          collateralCurrency={collateralCurrency}
        />
        <p className="text-shadow text-f5 mt-4">
          {translate('loan_calculator_choose_stableloan_length_note')}
        </p>
        <LoanLengthSelect
          loanLength={loanLength}
          onChangeLoanLength={setLoanLength}
          loanDurations={loanDurationsOptions}
        />
        <div className="mt-2 d-flex flex-row justify-content-between">
          <p className="text-shadow text-f5">{translate('loan_calculation_interest_breakdown')}</p>
          <p className="text-shadow text-f5">{translate('loan_calculation_pay_in_cash')}</p>
        </div>
        <div className="rounded-extra bg-light mt-3">
          <div className="d-flex flex-column p-0">
            <div className="p-3">
              <div className="d-flex flex-row justify-content-between">
                <p className="card-calculator__summary--item-note text-shadow text-f6">
                  {translate('loan_review_offer_loan_to_value_label')}
                </p>
                <b className="text-f6">{`${ltvPercentList[selectedLTV]}%`}</b>
              </div>
            </div>
            <hr className="m-0" />
            <div className="p-3">
              <div className="d-flex flex-row justify-content-between">
                <p className="card-calculator__summary--item-note text-shadow text-f6">
                  {translate('loan_review_offer_apr_label')}
                </p>
                <b className="text-f6">{`${calculatorForm?.apr}%`}</b>
              </div>
            </div>
            <hr className="m-0" />
            <div className="p-3">
              <div className="d-flex flex-row justify-content-between">
                <p className="card-calculator__summary--item-note text-shadow text-f6">
                  {translate('loan_review_offer_total_interest_label')}
                </p>

                <b className="text-f6 upper-case">{`${loanInterestValue} ${principalCurrency}`}</b>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-extra bg-light mt-3">
          <div className="d-flex flex-column p-0">
            <div className="p-3">
              <div className="d-flex flex-row justify-content-between">
                <p className="card-calculator__summary--item-note text-shadow text-f6">
                  {translate('loan_review_offer_total_payment_label')}
                </p>
                <b className="text-f6">
                  {`${formatNumberWithComma(
                    formatNumber(
                      parseFloat(calculatorForm?.principalAmountDecimal) +
                        parseFloat(loanInterestValue),
                      currencies[principalCurrency].decimal
                    )
                  )} ${principalCurrency?.toUpperCase()}`}
                </b>
              </div>
            </div>
          </div>
        </div>
      </ModalBodyLayout>
      <Tooltip
        id="payment-source-tooltip"
        place="bottom"
        variant="dark"
        opacity={1}
        className="p-4 rounded border-1 custom-tooltip">
        <b className="text-white text-f5">{translate('choose_payout_currency_info_modal_title')}</b>
        <p className="text-white text-f6 mt-2">
          {translate('choose_payout_currency_info_modal_content')}
        </p>
      </Tooltip>
      <Tooltip
        id="loan-calc-info-tooltip"
        place="bottom"
        variant="dark"
        opacity={1}
        className="p-4 rounded border-1 custom-tooltip">
        <b className="text-white text-f5">{translate('stableloan_calculator_info_modal_title')}</b>
        <p className="text-white text-f6 mt-2">
          {translate('stableloan_calculator_info_modal_content')}
        </p>
      </Tooltip>
    </>
  );
};

export default LoanCalculator;

LoanCalculator.propTypes = {
  screensKeys: PropTypes.object.isRequired,
  calculatorForm: PropTypes.object.isRequired,
  updateCalculatorForm: PropTypes.func.isRequired,
  openScreen: PropTypes.func.isRequired,
  wallets: PropTypes.array.isRequired,
  displayAddCollateral: PropTypes.func.isRequired,
  setExtraCollateralOptions: PropTypes.func.isRequired,
};
